import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notify, notifyError } from '../notify';

// Scraper accounts (the Reddit accounts + proxies the scraper reads Reddit
// with), for the platform's admins: add one, import many from cookie files, or
// change an account's status, proxy, cookie or notes. Cookies and proxy
// passwords are write-only: the list shows a masked proxy and what kind of
// cookie is stored.

const STATUSES = ['active', 'disabled', 'blocked'];
const STATUS_COLOR = { active: '#22c55e', disabled: '#888888', blocked: '#ef4444' };
const EMPTY_FORM = { username: '', proxy: '', cookie: '', notes: '' };
const PICKUP_NOTE = 'The cookie must come from a login made through this same proxy. The scraper picks changes up within about 10 minutes.';

function ago(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

function MatureContent({ account }) {
  if (account.shows_nsfw === null) return <span className="sub">NSFW: not checked yet</span>;
  const when = account.checked_at ? ` · checked ${ago(account.checked_at)}` : '';
  return (
    <span className="sub" style={{ color: account.shows_nsfw ? '#22c55e' : '#f59e0b' }} title={'The account\'s Reddit setting "Show mature content": only accounts that show NSFW build NSFW knowledge bases'}>
      {account.shows_nsfw ? 'Shows NSFW' : 'NSFW hidden'}{when}
    </span>
  );
}

// creating: every field is needed; editing: empty proxy / cookie keep the current ones.
function AccountForm({ initial, creating, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const save = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };
  return (
    <div className="form-grid" style={{ marginTop: '12px' }}>
      {creating && <input value={form.username} onChange={set('username')} placeholder="Reddit username" />}
      <input value={form.proxy} onChange={set('proxy')} placeholder={creating ? 'Proxy: host:port:user:pass or http://user:pass@host:port' : 'New proxy (leave empty to keep the current one)'} />
      <textarea rows={3} value={form.cookie} onChange={set('cookie')} placeholder={creating ? 'Cookie: the Cookie header, or the AdsPower cookie JSON export' : 'New cookie (leave empty to keep the current one)'} />
      <input value={form.notes} onChange={set('notes')} placeholder="Notes, e.g. adspower, US residential" />
      <div className="form-row">
        <button onClick={save} disabled={saving}>{saving ? 'Saving…' : creating ? 'Add account' : 'Save'}</button>
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
      <p className="sub">{PICKUP_NOTE}</p>
    </div>
  );
}

// An import row's line: what happens to that file's account
const IMPORT_STATES = {
  add: ['#22c55e', (r) => `proxy line ${r.line}: ${r.proxy}`],
  no_proxy: ['#f59e0b', () => 'needs a proxy: add a line'],
  skip: ['#ef4444', (r) => (r.line ? `proxy line ${r.line}: ${r.reason}` : r.reason)]
};

// Import from files: one account per cookie file (a zip of them), named by the
// file. The server reads the files and lists what it would do before anything
// is added; the pasted proxies go to the listed accounts in order.
function ImportForm({ onAdded, onCancel }) {
  const [files, setFiles] = useState([]);
  const [proxies, setProxies] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const latest = useRef(0); // only the newest answer is shown

  const send = (path, picked) => {
    const body = new FormData();
    picked.forEach(f => body.append('files', f));
    body.append('proxies', proxies);
    body.append('notes', notes);
    return axios.post(`${API}/api/scraper-accounts/${path}`, body);
  };
  const preview = async (picked) => {
    if (!picked.length) return;
    const ask = ++latest.current;
    try {
      const res = await send('import/preview', picked);
      if (ask === latest.current) setRows(res.data.rows);
    } catch (err) {
      if (ask === latest.current) { setRows(null); notifyError(err, 'Could not read the files'); }
    }
  };
  const add = async () => {
    const ask = ++latest.current;
    setBusy(true);
    try {
      const res = await send('import', files);
      notify(`${res.data.added} scraper account${res.data.added === 1 ? '' : 's'} added: the scraper starts using them within about 10 minutes`, 'success');
      onAdded();
    } catch (err) {
      if (ask === latest.current && err.response?.data?.rows) setRows(err.response.data.rows);
      notifyError(err, 'Could not add the accounts');
      setBusy(false);
    }
  };
  const count = (state) => (rows || []).filter(r => r.state === state).length;
  const ready = count('add');
  return (
    <div className="form-grid" style={{ marginTop: '12px' }}>
      <input type="file" multiple accept=".zip,.docx,.json,.txt"
        onChange={e => { const picked = [...e.target.files]; setFiles(picked); setRows(null); preview(picked); }} />
      <textarea rows={4} value={proxies} onChange={e => setProxies(e.target.value)} onBlur={() => preview(files)}
        placeholder="Proxies, one per line (host:port:user:pass or http://user:pass@host:port): line 1 goes to the first account below, and so on" />
      <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes for all of them, e.g. throwaway, US residential" />
      {rows && (
        <>
          <div className="sub">{ready} ready to add · {count('no_proxy')} need a proxy · {count('skip')} can't be added</div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {rows.map((r, i) => {
              const [color, describe] = IMPORT_STATES[r.state];
              return (
                <div key={`${i}-${r.file}`} className="sub" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#ddd' }}>{r.username}</strong>
                  <span style={{ color }}>{describe(r)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="form-row">
        <button onClick={add} disabled={busy || !ready}>{busy ? 'Adding…' : `Add ${ready} account${ready === 1 ? '' : 's'}`}</button>
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      <p className="sub">Only each file's reddit.com cookies are kept. The scraper checks every account when it picks it up (within about 10 minutes); a dead session shows its error in the list below.</p>
    </div>
  );
}

function AccountRow({ account, onSaved }) {
  const [editing, setEditing] = useState(false);
  const update = async (changes, done) => {
    try {
      await axios.put(`${API}/api/scraper-accounts/${account.id}`, changes);
      notify(done, 'success');
      setEditing(false);
      onSaved();
    } catch (err) {
      notifyError(err, 'Could not save the account');
    }
  };
  const saveForm = (form) => {
    const changes = { notes: form.notes };
    if (form.proxy.trim()) changes.proxy = form.proxy;
    if (form.cookie.trim()) changes.cookie = form.cookie;
    return update(changes, `${account.username} saved: the scraper uses the new settings within about 10 minutes`);
  };
  const setStatus = (status) => update({ status }, `${account.username} is ${status} now`);
  return (
    <div className="list-item" style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <strong>{account.username}</strong>
        <select value={account.status} onChange={e => setStatus(e.target.value)} style={{ flex: 'none', padding: '4px 8px', fontSize: '12px', color: STATUS_COLOR[account.status] }}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <MatureContent account={account} />
        <span style={{ flex: 1 }} />
        <button className="btn-secondary btn-small" onClick={() => setEditing(!editing)}>{editing ? 'Close' : 'Edit'}</button>
      </div>
      <div className="sub" style={{ marginTop: '6px' }}>
        {account.proxy} · cookie: {account.cookie} · {account.last_used_at ? `used ${ago(account.last_used_at)}` : 'never used'}
        {account.fail_count ? ` · ${account.fail_count} fails` : ''}{account.notes ? ` · ${account.notes}` : ''}
      </div>
      {account.last_error && <div className="sub" style={{ color: '#ef4444', marginTop: '4px' }}>{account.last_error}</div>}
      {editing && <AccountForm initial={{ ...EMPTY_FORM, notes: account.notes || '' }} onSave={saveForm} onCancel={() => setEditing(false)} />}
    </div>
  );
}

function ScraperAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(null); // 'add' | 'import': the form showing
  const toggle = (form) => setOpen(open === form ? null : form);

  const load = useCallback(() => axios.get(`${API}/api/scraper-accounts`)
    .then(res => setAccounts(res.data))
    .catch(err => (err.response && err.response.status === 403 ? setForbidden(true) : notifyError(err, 'Could not load the scraper accounts'))), []);
  useEffect(() => { load(); }, [load]);

  const add = async (form) => {
    try {
      await axios.post(`${API}/api/scraper-accounts`, form);
      notify(`${form.username.trim()} added: the scraper starts using it within about 5 minutes`, 'success');
      setOpen(null);
      load();
    } catch (err) {
      notifyError(err, 'Could not add the account');
    }
  };

  if (forbidden) return <div className="card"><p className="empty">Only the platform's admins manage scraper accounts.</p></div>;
  const active = accounts ? accounts.filter(a => a.status === 'active').length : 0;
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Scraper accounts{accounts ? ` · ${active} active of ${accounts.length}` : ''}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-small btn-secondary" onClick={() => toggle('import')}>{open === 'import' ? 'Close import' : 'Import from files'}</button>
          <button className="btn-small" onClick={() => toggle('add')}>{open === 'add' ? 'Cancel' : '+ Add scrape account'}</button>
        </div>
      </div>
      {open === 'add' && <AccountForm initial={EMPTY_FORM} creating onSave={add} onCancel={() => setOpen(null)} />}
      {open === 'import' && <ImportForm onAdded={() => { setOpen(null); load(); }} onCancel={() => setOpen(null)} />}
      {!accounts ? <p className="empty" style={{ marginTop: '12px' }}>Loading…</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
          {accounts.map(a => <AccountRow key={a.id} account={a} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

export default ScraperAccounts;
